import { MoneyError } from '#money/errors'

/**
 * Exact decimal arithmetic, in `bigint`. Internal to the package.
 *
 * Every number that passes through here is a magnitude plus a sign plus a scale, so a
 * `numeric` string from Postgres, an exchange rate with eight decimal places and a tax
 * percentage are all the same shape and none of them ever becomes a float. That matters
 * most for the one operation this package cannot borrow from Dinero: **division by an
 * exchange rate** (invariant 04). Dinero converts by multiplying by a rate, so expressing
 * "divide by 17,015" through its API means inverting the rate first — and the inverse of an
 * integer rate is a repeating decimal, so the loss happens before the conversion even runs.
 */

export type Decimal = {
  readonly negative: boolean
  /** The magnitude, unsigned. Value is `unscaled / 10 ** scale`. */
  readonly unscaled: bigint
  readonly scale: number
}

const DECIMAL = /^[+-]?\d+(?:\.\d+)?$/

export function parseDecimal(value: string, detail: Record<string, unknown> = {}): Decimal {
  if (!DECIMAL.test(value)) {
    throw new MoneyError('malformed_amount', `\`${value}\` is not a decimal amount.`, {
      ...detail,
      value,
    })
  }
  const negative = value.startsWith('-')
  const body = value.replace(/^[+-]/, '')
  const dot = body.indexOf('.')
  const whole = dot === -1 ? body : body.slice(0, dot)
  const fraction = dot === -1 ? '' : body.slice(dot + 1)
  return { negative, unscaled: BigInt(whole + fraction), scale: fraction.length }
}

export function isZeroDecimal(value: Decimal): boolean {
  return value.unscaled === 0n
}

const TEN = 10n

function pow10(n: number): bigint {
  return TEN ** BigInt(n)
}

/**
 * Rescale to `targetScale`, **rounding half away from zero**.
 *
 * Away from zero rather than half-even, and it is a decision rather than a default: a
 * quoted price and the invoice reissued from it must agree, and two call sites picking
 * different tie-breaks is exactly how they stop agreeing. Half-even would also break the
 * symmetry a credit note depends on — `round(-x) === -round(x)`.
 *
 * Returns **signed** minor units, so the caller does not reapply the sign.
 */
export function rescale(value: Decimal, targetScale: number): bigint {
  const magnitude = rescaleMagnitude(value, targetScale)
  return value.negative ? -magnitude : magnitude
}

function rescaleMagnitude(value: Decimal, targetScale: number): bigint {
  if (value.scale <= targetScale) return value.unscaled * pow10(targetScale - value.scale)

  const divisor = pow10(value.scale - targetScale)
  const quotient = value.unscaled / divisor
  const remainder = value.unscaled % divisor
  // `* 2n >= divisor` rather than a comparison against half, so nothing is halved and the
  // tie case stays exact.
  return remainder * 2n >= divisor ? quotient + 1n : quotient
}

/** Rescale only if exact; otherwise `undefined`, so the caller decides how to refuse. */
export function rescaleExact(value: Decimal, targetScale: number): bigint | undefined {
  if (value.scale <= targetScale) return rescale(value, targetScale)
  const divisor = pow10(value.scale - targetScale)
  if (value.unscaled % divisor !== 0n) return undefined
  const magnitude = value.unscaled / divisor
  return value.negative ? -magnitude : magnitude
}

/**
 * `(a * b) / c`, rounded half away from zero, entirely in integers.
 *
 * The one primitive behind both the tax multiply and the exchange-rate divide. Doing them as
 * one fused operation rather than a multiply followed by a divide is what keeps the
 * intermediate exact — there is no place for a partial result to be rounded early.
 */
export function mulDivRound(a: bigint, b: bigint, c: bigint): bigint {
  const negative = (a < 0n !== b < 0n) !== c < 0n
  const magnitude = (a < 0n ? -a : a) * (b < 0n ? -b : b)
  const divisor = c < 0n ? -c : c
  const quotient = magnitude / divisor
  const remainder = magnitude % divisor
  const rounded = remainder * 2n >= divisor ? quotient + 1n : quotient
  return negative ? -rounded : rounded
}

export { pow10 }
